/**
 * Book Appointment Trigger Component
 *
 * Trigger: "booked a visit"
 * Shows: service select + optional staff multi-select
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchServicesOptions } from 'src/models/service/service';
import { fetchTrainers } from 'src/models/user/user';
import { User, getUserDisplayName } from 'src/models/user/utils';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { TriggerComponentProps } from '../automationTriggers';

const getTrainerIds = (content: string | string[] | undefined): number[] => {
  if (!content) return [];
  const values = Array.isArray(content) ? content : [content];
  return values
    .map((value) => parseInt(String(value), 10))
    .filter((value) => !Number.isNaN(value));
};

export const BookAppointmentTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const { data: services = [] } = useQuery({
    queryKey: ['services', 'options'],
    queryFn: () => fetchServicesOptions(),
    staleTime: 60000,
  });

  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers', 'book-appointment'],
    queryFn: () => fetchTrainers(),
    staleTime: 60000,
  });

  const serviceOptions = services.map((service) => ({
    value: String(service.id),
    label: service.name,
  }));

  const selectedService = services.find(
    (service) => String(service.id) === String(automation.reminderTypeId)
  );
  const selectedTrainerIds = getTrainerIds(automation.content);
  const selectedTrainers = trainers.filter((trainer) =>
    selectedTrainerIds.includes(parseInt(trainer.id.toString(), 10))
  );

  if (readOnly) {
    const serviceName =
      selectedService?.name || t('automations.triggers.anyServiceCap');
    const trainerNames = selectedTrainers
      .map((trainer) => getUserDisplayName(trainer))
      .join(', ');

    if (trainerNames) {
      return (
        <span>
          {t('automations.triggers.bookedVisitWithStaff', {
            service: serviceName,
            staff: trainerNames,
          })}
        </span>
      );
    }

    return (
      <span>{t('automations.triggers.bookedVisitFor', { service: serviceName })}</span>
    );
  }

  return (
    <Stack spacing={2} direction="row" alignItems="center" flexWrap="wrap">
      <Box sx={{ maxWidth: 300 }}>
        <FormSelect
          name={`automations.${index}.reminderTypeId`}
          label={t('automations.triggerLabels.service')}
          options={serviceOptions}
          prompt={t('automations.triggers.anyServiceCap')}
        />
      </Box>
      <Box>{t('automations.triggers.with')}</Box>
      <Box sx={{ minWidth: 250, flex: 1 }}>
        <Autocomplete<User, true>
          multiple
          options={trainers}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : getUserDisplayName(option)
          }
          value={selectedTrainers}
          onChange={(_, newValue) => {
            setFieldValue(
              `automations.${index}.content`,
              newValue.map((trainer) => parseInt(trainer.id.toString(), 10))
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('automations.triggers.selectStaff')}
              size="small"
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>
    </Stack>
  );
};
